import { useEffect, useState, useCallback, useRef } from "react";
import { Plus, Pencil, Trash2, Loader2, X, Save, Upload, Image as ImageIcon, Star, Home, ChevronLeft, ChevronRight, Eye, EyeOff, FileEdit, AlertCircle, RotateCcw } from "lucide-react";
import { api, formatApiErrorDetail, formatPrice } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { SPECIES_CATEGORIES, ALL_CATEGORIES, catLabel } from "@/lib/taxonomy";
import { toast } from "sonner";

const PETS = ["todos", "perro", "gato", "conejos-roedores", "aves", "peces"];

const EMPTY = { name: "", price: 0, category: "accesorios", categories: [], pet_type: "todos", presentation: "", size: "", discount: 0, description: "", image_url: "", images: [], sku: "", variants: [], in_stock: true, featured: false, published: true };

const hasPhoto = (p) => (p.images && p.images.length > 0) || !!(p.image_url || "").trim();
const isPending = (p) => p.published === false || !hasPhoto(p);
const catsLabel = (p) => {
  const list = (p.categories && p.categories.length) ? p.categories : (p.category ? [p.category] : []);
  return list.map(catLabel).join(", ") || "—";
};

export const ProductsManager = ({ initialView = "published", onChange }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [view, setView] = useState(initialView);
  const [trash, setTrash] = useState([]);
  const fileRef = useRef();

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/admin/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(data.url);
      }
      setEditing((p) => {
        const images = [...(p.images || []), ...uploaded];
        return { ...p, images, image_url: p.image_url || images[0] };
      });
      toast.success(uploaded.length > 1 ? `${uploaded.length} imágenes subidas` : "Imagen subida");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo subir la imagen");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const setCover = (url) => setEditing((p) => ({ ...p, image_url: url }));
  const removeImage = (url) => setEditing((p) => {
    const images = (p.images || []).filter((u) => u !== url);
    const image_url = p.image_url === url ? (images[0] || "") : p.image_url;
    return { ...p, images, image_url };
  });
  const moveImage = (idx, dir) => setEditing((p) => {
    const images = [...(p.images || [])];
    const j = idx + dir;
    if (j < 0 || j >= images.length) return p;
    [images[idx], images[j]] = [images[j], images[idx]];
    return { ...p, images };
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/products", { params: { include_all: true } });
      setProducts(data);
      onChange?.();
    } finally {
      setLoading(false);
    }
  }, [onChange]);

  const loadTrash = useCallback(async () => {
    try {
      const { data } = await api.get("/products/trash");
      setTrash(data);
    } catch { /* noop */ }
  }, []);

  useEffect(() => { load(); loadTrash(); }, [load, loadTrash]);

  const openNew = () => { setEditing({ ...EMPTY }); setOpen(true); };
  const openDraft = () => { setEditing({ ...EMPTY, published: false }); setOpen(true); };
  const openEdit = (p) => {
    const images = (p.images && p.images.length) ? [...p.images] : (p.image_url ? [p.image_url] : []);
    const categories = (p.categories && p.categories.length) ? [...p.categories] : (p.category ? [p.category] : []);
    setEditing({ ...p, images, categories, image_url: p.image_url || images[0] || "" });
    setOpen(true);
  };

  const save = async () => {
    if (!editing.name.trim()) { toast.error("El nombre es obligatorio"); return; }
    setSaving(true);
    try {
      const images = (editing.images || []).filter(Boolean);
      const image_url = editing.image_url || images[0] || "";
      const categories = (editing.categories && editing.categories.length)
        ? editing.categories.filter(Boolean)
        : (editing.category ? [editing.category] : []);
      const category = categories[0] || editing.category || "accesorios";
      const payload = { ...editing, images, image_url, categories, category, price: Number(editing.price) || 0, discount: Number(editing.discount) || 0 };
      payload.variants = (editing.variants || [])
        .filter((v) => v && v.name && v.name.trim())
        .map((v) => ({ name: v.name.trim(), sku: (v.sku || "").trim(), price: Number(v.price) || 0 }));
      if (editing.id) await api.put(`/products/${editing.id}`, payload);
      else await api.post("/products", payload);
      toast.success("Producto guardado");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const toggleFeatured = async (p) => {
    const next = !p.featured;
    setProducts((list) => list.map((x) => (x.id === p.id ? { ...x, featured: next } : x)));
    try {
      await api.patch(`/products/${p.id}/featured`, { featured: next });
      toast.success(next ? "Añadido a Favoritos de la casa" : "Quitado de Favoritos");
    } catch {
      setProducts((list) => list.map((x) => (x.id === p.id ? { ...x, featured: !next } : x)));
      toast.error("No se pudo actualizar");
    }
  };

  const togglePublished = async (p) => {
    const next = !(p.published !== false && hasPhoto(p));
    if (next && !hasPhoto(p)) { toast.error("Agrega una foto antes de publicar este producto"); return; }
    setProducts((list) => list.map((x) => (x.id === p.id ? { ...x, published: next } : x)));
    try {
      await api.patch(`/products/${p.id}/published`, { published: next });
      toast.success(next ? "Producto publicado en la tienda" : "Producto ocultado (pendiente por publicar)");
    } catch (err) {
      setProducts((list) => list.map((x) => (x.id === p.id ? { ...x, published: !next } : x)));
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo actualizar");
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`¿Mover "${p.name}" a la papelera? Podrás recuperarlo por 30 días.`)) return;
    try {
      await api.delete(`/products/${p.id}`);
      toast.success("Movido a la papelera");
      load(); loadTrash();
    } catch {
      toast.error("No se pudo eliminar");
    }
  };

  const restore = async (p) => {
    try {
      await api.post(`/products/${p.id}/restore`);
      toast.success("Producto restaurado");
      load(); loadTrash();
    } catch {
      toast.error("No se pudo restaurar");
    }
  };

  const deleteForever = async (p) => {
    if (!window.confirm(`¿Eliminar "${p.name}" DEFINITIVAMENTE? Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/products/${p.id}/permanent`);
      toast.success("Eliminado definitivamente");
      loadTrash();
    } catch {
      toast.error("No se pudo eliminar");
    }
  };

  const daysLeft = (deletedAt) => {
    if (!deletedAt) return 30;
    const ms = new Date(deletedAt).getTime() + 30 * 86400000 - Date.now();
    return Math.max(0, Math.ceil(ms / 86400000));
  };

  const set = (k, v) => setEditing((e) => ({ ...e, [k]: v }));

  const publishedList = products.filter((p) => !isPending(p));
  const pendingList = products.filter((p) => isPending(p));
  const shown = view === "published" ? publishedList : pendingList;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div className="flex gap-2">
          <button data-testid="view-published" onClick={() => setView("published")} className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-2xl text-sm font-semibold border transition-all ${view === "published" ? "bg-coral text-white border-coral" : "bg-white text-charcoal border-[#E3E7E2] hover:bg-peach/50"}`}>
            <Eye className="w-4 h-4" /> Publicados ({publishedList.length})
          </button>
          <button data-testid="view-pending" onClick={() => setView("pending")} className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-2xl text-sm font-semibold border transition-all ${view === "pending" ? "bg-coral text-white border-coral" : "bg-white text-charcoal border-[#E3E7E2] hover:bg-peach/50"}`}>
            <AlertCircle className="w-4 h-4" /> Pendientes por publicar ({pendingList.length})
          </button>
          <button data-testid="view-trash" onClick={() => setView("trash")} className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-2xl text-sm font-semibold border transition-all ${view === "trash" ? "bg-coral text-white border-coral" : "bg-white text-charcoal border-[#E3E7E2] hover:bg-peach/50"}`}>
            <Trash2 className="w-4 h-4" /> Papelera ({trash.length})
          </button>
        </div>
        <div className="flex gap-2">
          <button data-testid="add-draft-button" onClick={openDraft} className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-2xl bg-white border border-[#E3E7E2] text-charcoal font-semibold hover:bg-sage/40 active:scale-95 transition-all">
            <FileEdit className="w-4 h-4" /> Borrador
          </button>
          <button data-testid="add-product-button" onClick={openNew} className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-2xl bg-coral text-white font-semibold hover:bg-coral-dark active:scale-95 transition-all">
            <Plus className="w-4 h-4" /> Nuevo
          </button>
        </div>
      </div>

      {view === "pending" && (
        <div className="rounded-2xl bg-peach/50 border border-coral/30 p-3 text-sm text-charcoal flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-coral shrink-0 mt-0.5" />
          <span>Aquí están tus borradores y los productos sin foto. No aparecen en la tienda hasta que agregues una foto y los publiques con el ícono del ojo.</span>
        </div>
      )}

      {view === "trash" && (
        <>
          <div className="rounded-2xl bg-cream-2 border border-[#E3E7E2] p-3 text-sm text-charcoal flex items-start gap-2">
            <Trash2 className="w-4 h-4 text-muted-moss shrink-0 mt-0.5" />
            <span>Los productos eliminados se guardan aquí <b>30 días</b>. Puedes restaurarlos o eliminarlos definitivamente.</span>
          </div>
          {trash.length === 0 ? (
            <div className="text-center py-14 text-muted-moss text-sm">La papelera está vacía.</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {trash.map((p) => (
                <div key={p.id} data-testid={`trash-product-${p.id}`} className="flex gap-3 bg-white rounded-2xl border border-[#E3E7E2] p-3">
                  {hasPhoto(p) ? (
                    <img src={p.image_url || p.images?.[0]} alt={p.name} className="w-16 h-16 rounded-xl object-cover bg-cream-2 shrink-0 opacity-70" />
                  ) : (
                    <div className="w-16 h-16 rounded-xl bg-cream-2 border border-dashed border-[#E3E7E2] grid place-items-center text-muted-moss shrink-0"><ImageIcon className="w-6 h-6" /></div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm text-charcoal line-clamp-1">{p.name}</p>
                    <p className="text-xs text-muted-moss">{catLabel(p.category)} · {p.pet_type}</p>
                    <p className="text-[11px] text-coral font-semibold mt-0.5">Se borra en {daysLeft(p.deleted_at)} día(s)</p>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <button data-testid={`restore-product-${p.id}`} onClick={() => restore(p)} title="Restaurar" className="inline-flex items-center gap-1 px-2.5 h-8 rounded-lg bg-teal/40 text-charcoal text-xs font-semibold hover:bg-teal/60"><RotateCcw className="w-3.5 h-3.5" /> Restaurar</button>
                    <button data-testid={`delete-forever-${p.id}`} onClick={() => deleteForever(p)} title="Eliminar definitivamente" className="inline-flex items-center gap-1 px-2.5 h-8 rounded-lg bg-cream text-destructive text-xs font-semibold hover:bg-destructive/10"><Trash2 className="w-3.5 h-3.5" /> Borrar</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {view !== "trash" && (loading ? (
        <div className="grid place-items-center py-16"><Loader2 className="w-7 h-7 animate-spin text-coral" /></div>
      ) : shown.length === 0 ? (
        <div className="text-center py-14 text-muted-moss text-sm">{view === "published" ? "Aún no hay productos publicados." : "No hay productos pendientes. ¡Todo publicado! 🎉"}</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {shown.map((p) => {
            const photo = hasPhoto(p);
            const published = p.published !== false && photo;
            return (
            <div key={p.id} data-testid={`admin-product-${p.id}`} className="flex gap-3 bg-white rounded-2xl border border-[#E3E7E2] p-3">
              {photo ? (
                <img src={p.image_url || p.images?.[0]} alt={p.name} className="w-16 h-16 rounded-xl object-cover bg-cream-2 shrink-0" />
              ) : (
                <div className="w-16 h-16 rounded-xl bg-cream-2 border border-dashed border-[#E3E7E2] grid place-items-center text-muted-moss shrink-0"><ImageIcon className="w-6 h-6" /></div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <p className="font-semibold text-sm text-charcoal line-clamp-1">{p.name}</p>
                  {!published && <span data-testid={`badge-pending-${p.id}`} className="shrink-0 text-[10px] font-bold text-coral bg-peach rounded-full px-1.5 py-0.5">{photo ? "Oculto" : "Sin foto"}</span>}
                </div>
                <p className="text-xs text-muted-moss">{catsLabel(p)} · {p.pet_type} · {p.presentation || p.size || "—"}</p>
                <p className="font-bold text-coral text-sm mt-0.5">{formatPrice(p.price, p.currency)}</p>
              </div>
              <div className="flex flex-col gap-1.5">
                <button data-testid={`toggle-published-${p.id}`} disabled={!photo} title={!photo ? "Agrega una foto para poder publicar" : (published ? "Ocultar de la tienda" : "Publicar en la tienda")} onClick={() => togglePublished(p)} className={`grid place-items-center w-8 h-8 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${published ? "bg-teal/40 text-charcoal hover:bg-teal/60" : "bg-cream text-muted-moss hover:bg-sage/50"}`}>
                  {published ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                </button>
                <button data-testid={`toggle-featured-${p.id}`} title={p.featured ? "En Favoritos de la casa" : "Marcar como favorito del inicio"} onClick={() => toggleFeatured(p)} className={`grid place-items-center w-8 h-8 rounded-lg transition-colors ${p.featured ? "bg-coral text-white" : "bg-cream text-muted-moss hover:bg-peach/50"}`}>
                  <Star className={`w-4 h-4 ${p.featured ? "fill-current" : ""}`} />
                </button>
                <button data-testid={`edit-product-${p.id}`} onClick={() => openEdit(p)} className="grid place-items-center w-8 h-8 rounded-lg bg-cream text-charcoal hover:bg-sage/50"><Pencil className="w-4 h-4" /></button>
                <button data-testid={`delete-product-${p.id}`} onClick={() => remove(p)} className="grid place-items-center w-8 h-8 rounded-lg bg-cream text-muted-moss hover:text-destructive hover:bg-destructive/10"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
            );
          })}
        </div>
      ))}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg rounded-3xl bg-cream border-[#E3E7E2] max-h-[92vh] overflow-y-auto">
          <DialogTitle className="font-display text-xl font-bold text-charcoal">{editing?.id ? "Editar producto" : "Nuevo producto"}</DialogTitle>
          <DialogDescription className="sr-only">Formulario de producto</DialogDescription>
          {editing && (
            <div className="space-y-3">
              <Field label="Nombre"><input data-testid="product-form-name" value={editing.name} onChange={(e) => set("name", e.target.value)} className="inp" /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Precio base"><input data-testid="product-form-price" type="number" value={editing.price} onChange={(e) => set("price", e.target.value)} className="inp" /></Field>
                <Field label="Presentación"><input value={editing.presentation || ""} onChange={(e) => set("presentation", e.target.value)} className="inp" placeholder="15 kg" /></Field>
              </div>
              <Field label="SKU / Código"><input data-testid="product-form-sku" value={editing.sku || ""} onChange={(e) => set("sku", e.target.value)} className="inp" placeholder="Ej: AL-PP" /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Tamaño"><input data-testid="product-form-size" value={editing.size || ""} onChange={(e) => set("size", e.target.value)} className="inp" placeholder="Ej: Grande / 3 kg" /></Field>
                <Field label="Descuento (%)"><input data-testid="product-form-discount" type="number" min="0" max="100" value={editing.discount || 0} onChange={(e) => set("discount", e.target.value)} className="inp" placeholder="0" /></Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Mascota">
                  <select value={editing.pet_type} onChange={(e) => set("pet_type", e.target.value)} className="inp">
                    {PETS.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </Field>
                <Field label="Categoría principal">
                  <select
                    data-testid="product-form-primary-category"
                    value={(editing.categories && editing.categories[0]) || editing.category || ""}
                    onChange={(e) => {
                      const primary = e.target.value;
                      const rest = (editing.categories || []).filter((c) => c !== primary);
                      set("categories", [primary, ...rest]);
                      set("category", primary);
                    }}
                    className="inp"
                  >
                    {Array.from(new Set([...(SPECIES_CATEGORIES[editing.pet_type] || ALL_CATEGORIES), ...(editing.categories || [])])).map((c) => <option key={c} value={c}>{catLabel(c)}</option>)}
                  </select>
                </Field>
              </div>
              <Field label="Categorías (puede pertenecer a varias)">
                <div data-testid="product-form-categories" className="flex flex-wrap gap-2">
                  {Array.from(new Set([...(SPECIES_CATEGORIES[editing.pet_type] || ALL_CATEGORIES), ...(editing.categories || [])])).map((c) => {
                    const selected = (editing.categories || []).includes(c);
                    return (
                      <button
                        type="button"
                        key={c}
                        data-testid={`category-toggle-${c}`}
                        onClick={() => {
                          const current = editing.categories || [];
                          let next;
                          if (selected) {
                            next = current.filter((x) => x !== c);
                          } else {
                            next = [...current, c];
                          }
                          set("categories", next);
                          set("category", next[0] || "");
                        }}
                        className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${selected ? "bg-coral text-white border-coral" : "bg-white text-charcoal border-[#E3E7E2] hover:bg-peach/50"}`}
                      >
                        {catLabel(c)}
                      </button>
                    );
                  })}
                </div>
              </Field>
              <Field label="Descripción adicional"><textarea value={editing.description || ""} onChange={(e) => set("description", e.target.value)} rows={2} className="inp resize-none" /></Field>
              <Field label="Galería de fotos">
                <div className="space-y-3">
                  {(editing.images || []).length > 0 && (
                    <div className="grid grid-cols-3 gap-2">
                      {(editing.images || []).map((url, i) => {
                        const isCover = editing.image_url === url;
                        return (
                          <div key={url + i} data-testid={`gallery-thumb-${i}`} className={`relative group rounded-xl overflow-hidden border-2 ${isCover ? "border-coral" : "border-[#E3E7E2]"}`}>
                            <img src={url} alt="" className="w-full aspect-square object-cover bg-cream-2" />
                            {isCover && <span className="absolute top-1 left-1 text-[9px] font-bold bg-coral text-white px-1.5 py-0.5 rounded-full">Portada</span>}
                            <div className="absolute inset-x-0 bottom-0 flex items-center justify-between p-1 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                              <div className="flex gap-0.5">
                                <button type="button" onClick={() => moveImage(i, -1)} disabled={i === 0} className="grid place-items-center w-6 h-6 rounded bg-white/90 text-charcoal disabled:opacity-30"><ChevronLeft className="w-3.5 h-3.5" /></button>
                                <button type="button" onClick={() => moveImage(i, 1)} disabled={i === (editing.images.length - 1)} className="grid place-items-center w-6 h-6 rounded bg-white/90 text-charcoal disabled:opacity-30"><ChevronRight className="w-3.5 h-3.5" /></button>
                              </div>
                              <div className="flex gap-0.5">
                                {!isCover && <button type="button" data-testid={`set-cover-${i}`} onClick={() => setCover(url)} title="Usar como portada" className="grid place-items-center w-6 h-6 rounded bg-white/90 text-charcoal"><Home className="w-3.5 h-3.5" /></button>}
                                <button type="button" data-testid={`remove-image-${i}`} onClick={() => removeImage(url)} title="Quitar" className="grid place-items-center w-6 h-6 rounded bg-white/90 text-destructive"><X className="w-3.5 h-3.5" /></button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {(editing.images || []).length === 0 && (
                    <div className="w-full h-20 rounded-xl bg-cream-2 border border-dashed border-[#E3E7E2] grid place-items-center text-muted-moss text-xs gap-1"><ImageIcon className="w-6 h-6" /> Sin fotos aún</div>
                  )}
                  <input ref={fileRef} type="file" multiple accept="image/png,image/jpeg,image/webp" className="hidden" onChange={onUpload} />
                  <button type="button" data-testid="product-image-upload" onClick={() => fileRef.current?.click()} disabled={uploading} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white border border-[#E3E7E2] text-sm font-semibold text-charcoal hover:bg-sage/40 disabled:opacity-60">
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Subir fotos
                  </button>
                  <p className="text-[11px] text-muted-moss">Sube varias fotos. La primera es la portada; usa el ícono de casa para cambiarla y las flechas para reordenar.</p>
                </div>
              </Field>

              <div className="rounded-2xl border border-[#E3E7E2] bg-white p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-charcoal">Variantes (talla, presentación...)</span>
                  <button data-testid="add-variant-button" onClick={() => set("variants", [...(editing.variants || []), { name: "", sku: "", price: "" }])} className="inline-flex items-center gap-1 text-xs font-semibold text-coral hover:underline"><Plus className="w-3.5 h-3.5" /> Agregar</button>
                </div>
                {(editing.variants || []).length === 0 ? (
                  <p className="text-xs text-muted-moss">Sin variantes. El producto se agrega directo al carrito.</p>
                ) : (
                  <div className="space-y-2">
                    {(editing.variants || []).map((v, i) => (
                      <div key={i} className="flex gap-2 items-center">
                        <input value={v.name} onChange={(e) => { const nv = [...editing.variants]; nv[i] = { ...nv[i], name: e.target.value }; set("variants", nv); }} placeholder="Nombre (ej: 3 kg)" className="inp flex-1" />
                        <input value={v.sku} onChange={(e) => { const nv = [...editing.variants]; nv[i] = { ...nv[i], sku: e.target.value }; set("variants", nv); }} placeholder="SKU" className="inp w-24" />
                        <input type="number" value={v.price} onChange={(e) => { const nv = [...editing.variants]; nv[i] = { ...nv[i], price: e.target.value }; set("variants", nv); }} placeholder="Precio" className="inp w-24" />
                        <button onClick={() => set("variants", editing.variants.filter((_, idx) => idx !== i))} className="grid place-items-center w-8 h-8 rounded-lg text-muted-moss hover:text-destructive shrink-0"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.in_stock} onChange={(e) => set("in_stock", e.target.checked)} className="w-4 h-4 accent-[#E08A7E]" /> En stock</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.featured} onChange={(e) => set("featured", e.target.checked)} className="w-4 h-4 accent-[#E08A7E]" /> Destacado</label>
                <label className="flex items-center gap-2 text-sm"><input data-testid="product-form-published" type="checkbox" checked={editing.published !== false} onChange={(e) => set("published", e.target.checked)} className="w-4 h-4 accent-[#E08A7E]" /> Publicado en la tienda</label>
              </div>
              {editing.published !== false && !((editing.images && editing.images.length) || (editing.image_url || "").trim()) && (
                <p className="text-xs text-coral flex items-center gap-1.5"><AlertCircle className="w-3.5 h-3.5" /> Sin foto quedará en "Pendientes por publicar" hasta que agregues una.</p>
              )}
              <button data-testid="product-form-save" onClick={save} disabled={saving} className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl bg-coral text-white font-bold hover:bg-coral-dark active:scale-95 transition-all disabled:opacity-60">
                {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />} {editing.published === false ? "Guardar borrador" : "Guardar"}
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs font-semibold uppercase tracking-wide text-muted-moss">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);
